import './image.scss';

import NextImage from 'next/image';
import React from 'react';

import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { formatDuration } from '@sola/utils/video.js';

import {
  getAspectHeight,
  getAspectWidth,
  getImageVariant,
} from '../../utils/image-utils';

type ImageProps = {
  image?: {
    url: string;
    alt?: null | string;
    ratio?: string | undefined;
    height?: number;
    width?: number;
    focus?: { x: string; y: string };
  };
  objectPosition?: string;
  objectFit?: 'cover' | 'contain';
  lazyLoad?: 'eager' | 'lazy';
  priority?: boolean;
  rounded?: string;
  aspectRatio?: string;
  aspectRatioSm?: string;
  aspectRatioMd?: string;
  aspectRatioLg?: string;
  className?: string;
  hasPlaceholder?: boolean;
  isVideoThumbnail?: boolean;
  videoDuration?: number;
};

// const blurImage = async (imageUrl: string) => {
//   const extension = imageUrl.includes('.') ? imageUrl.split('.').at(-1) : 'jpg';
//   const fetchImage = await fetch(`${imageUrl}?w=10&q=100`);
//   const responseBuffer = await fetchImage.arrayBuffer();
//   const base64 = Buffer.from(responseBuffer).toString('base64');
//   const dynamicBlurURL = `data:image/${extension};base64,${base64}`;
//   return dynamicBlurURL;
// };

export const Image: React.FC<ImageProps> = ({
  image,
  lazyLoad,
  priority,
  aspectRatio,
  aspectRatioSm,
  aspectRatioMd,
  aspectRatioLg,
  objectPosition = 'center',
  objectFit,
  rounded,
  className,
  hasPlaceholder = false,
  isVideoThumbnail = false,
  videoDuration,
}) => {
  const width =
    aspectRatio === 'contain' && image?.width
      ? image.width
      : getAspectWidth(aspectRatio as string);
  const height =
    aspectRatio === 'contain' && image?.height
      ? image.height
      : getAspectHeight(aspectRatio as string);
  const isSvg = image?.url?.endsWith('.svg');
  // const [blurDataURL, setBlurDataURL] = useState<string | undefined>(undefined);

  // @TODO: Load this blur image on the server, not the client.
  // useEffect(() => {
  //   if (image?.url) {
  //     blurImage(image.url)
  //       .then((url) => {
  //         setBlurDataURL(url);
  //       })
  //       .catch(() => {});
  //   }
  // }, [image?.url]);

  if (!image && !hasPlaceholder) {
    return;
  }

  const shouldUseDimensions = aspectRatio || objectFit === 'contain';

  const imgProps =
    shouldUseDimensions && width && height ? { width, height } : { fill: true };

  return (
    <div
      className={cn(
        `Image`,
        {
          [`has-ratio`]: aspectRatio || aspectRatioMd || aspectRatioLg,
          [`aspect-ratio-${aspectRatio}`]: aspectRatio,
          [`@sm:aspect-ratio-${aspectRatioSm}`]: aspectRatioSm,
          [`@md:aspect-ratio-${aspectRatioMd}`]: aspectRatioMd,
          [`@lg:aspect-ratio-${aspectRatioLg}`]: aspectRatioLg,
          [`rounded-${rounded}`]: rounded,
        },
        className,
      )}
      style={
        !image
          ? {
              backgroundImage: `url(/placeholder.svg)`,
              backgroundSize: '20px',
            }
          : undefined
      }
    >
      {isVideoThumbnail && (
        <div className="Image__video-thumb">
          <div className="Image__video-thumb__overlay" />
          <div className="Image__video-thumb__info">
            {videoDuration && (
              <div className="label-xl label-potent p-comfy-lg">
                {formatDuration(videoDuration)}
              </div>
            )}
          </div>
          <div className="Image__video-thumb__play">
            <ArkIcon iconName="play" />
          </div>
        </div>
      )}
      {image && (
        <NextImage
          className={cn(
            'Image',
            getImageVariant(objectPosition as string, rounded as string),
            objectFit || (isSvg ? 'contain' : 'cover'),
            `${className}-img`,
          )}
          loading={priority ? 'eager' : lazyLoad}
          fetchPriority={priority ? 'high' : undefined}
          src={image?.url as string}
          alt={image?.alt as string}
          // placeholder={blurDataURL ? 'blur' : undefined}
          // blurDataURL={blurDataURL}
          // @TODO: We need to provide sizes for grid usage. See: https://nextjs.org/docs/app/api-reference/components/image#sizes
          // fill naturally if using an aspect ratio, needed for grids
          sizes="100vw"
          {...imgProps}
          style={{
            objectPosition: image.focus
              ? `${image.focus.x} ${image.focus.y}`
              : undefined,
          }}
        />
      )}
    </div>
  );
};
